import styled, { css } from 'styled-components'

import { AnalyticsEvent } from 'analytics/performance'
import { SvgIconType } from 'components/styled/icon/svg'
import { TextVariant } from 'components/styled/menu/menu-item/menu-button-content'

const ButtonVariants = {
  BASIC: 'basic',
  PRIMARY_BASIC: 'primaryBasic',
  BASIC_SECONDARY: 'basicSecondary',
  PRIMARY_GHOST: 'primaryGhost',
  SECONDARY: 'secondary',
} as const

export type ButtonVariant = typeof ButtonVariants[keyof typeof ButtonVariants]

export interface BaseMenuButtonProps {
  variant?: ButtonVariant
  icon?: string
  svgIcon?: SvgIconType
  iconRight?: string
  iconRightClose?: string
  text: string | React.ReactNode
  useOutlinedIcon?: boolean
  /** Use Material Symbols font for `icon` (required for names like bookmark_check). */
  iconUseSymbols?: boolean
  closeMenuOnClick?: boolean
  event?: AnalyticsEvent
  title?: string
  disabled?: boolean
  textVariant?: TextVariant
  buttonStyle?: 'default' | 'rounded'
}

export interface BaseMenuButtonStyleProps {
  $variant: ButtonVariant
  $isSelected: boolean
}

const BasicStyle = css<{ $isSelected: boolean }>`
  /* double ampersands added to override some old styles
  to be removed later */
  && {
    color: ${({ theme }) => theme.color.grey90};

    svg {
      fill: ${({ theme }) => theme.color.grey90};
    }

    ${({ $isSelected }) =>
      $isSelected &&
      css`
        background-color: ${({ theme }) => theme.color.primary05};
        color: ${({ theme }) => theme.color.primary120};

        svg {
          fill: ${({ theme }) => theme.color.primary120};
        }
      `}

    &:hover {
      text-decoration: none;
      background-color: ${({ theme }) => theme.color.primary05};
      color: ${({ theme }) => theme.color.primary120};

      svg {
        fill: ${({ theme }) => theme.color.primary120};
      }
    }

    &:active {
      text-decoration: none;
      background-color: ${({ theme }) => theme.color.primary10};
      color: ${({ theme }) => theme.color.primary130};

      svg {
        fill: ${({ theme }) => theme.color.primary130};
      }
    }

    &.focus-visible {
      border-color: ${({ theme }) => theme.color.primary110};
      color: ${({ theme }) => theme.color.primary110};

      svg {
        fill: ${({ theme }) => theme.color.primary110};
      }
    }

    &:disabled {
      background-color: transparent;
      color: ${({ theme }) => theme.color.primary30};

      svg {
        fill: ${({ theme }) => theme.color.primary30};
      }

      > * {
        opacity: 0.3;
      }
    }
  }
`

const PrimaryBasicStyle = css<{ $isSelected: boolean }>`
  ${BasicStyle};
  && {
    color: ${({ theme }) => theme.color.primary110};
  }
`

const BasicSecondaryStyle = css<{ $isSelected: boolean }>`
  /* double ampersands added to override some old styles
  to be removed later */
  && {
    color: ${({ theme }) => theme.color.grey90};

    svg {
      fill: ${({ theme }) => theme.color.grey90};
    }

    ${({ $isSelected }) =>
      $isSelected &&
      css`
        background-color: ${({ theme }) => theme.color.grey07};
        color: ${({ theme }) => theme.color.grey100};

        svg {
          fill: ${({ theme }) => theme.color.grey100};
        }
      `}

    &:hover {
      text-decoration: none;
      background-color: ${({ theme }) => theme.color.grey07};
      color: ${({ theme }) => theme.color.grey100};

      svg {
        fill: ${({ theme }) => theme.color.grey100};
      }
    }

    &:active {
      text-decoration: none;
      background-color: ${({ theme }) => theme.color.grey10};
      color: ${({ theme }) => theme.color.grey100};

      svg {
        fill: ${({ theme }) => theme.color.grey100};
      }
    }

    &:disabled {
      background-color: transparent;
      color: ${({ theme }) => theme.color.grey30};

      svg {
        fill: ${({ theme }) => theme.color.grey30};
      }

      > * {
        opacity: 0.3;
      }
    }

    &.focus-visible {
      border-color: ${({ theme }) => theme.color.grey70};
    }
  }
`

const PrimaryGhostStyle = css`
  border-color: ${({ theme }) => theme.color.primary110};

  && {
    color: ${({ theme }) => theme.color.primary110};
  }

  svg {
    fill: ${({ theme }) => theme.color.primary110};
  }

  &:hover {
    border-color: ${({ theme }) => theme.color.primary120};
    color: ${({ theme }) => theme.color.primary120};

    svg {
      fill: ${({ theme }) => theme.color.primary120};
    }
  }

  &:active {
    border-color: ${({ theme }) => theme.color.primary130};
    color: ${({ theme }) => theme.color.primary130};

    svg {
      fill: ${({ theme }) => theme.color.primary130};
    }
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.primary30};
    color: ${({ theme }) => theme.color.primary30};

    svg {
      fill: ${({ theme }) => theme.color.primary30};
    }
  }

  &.focus-visible {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.color.white},
      0 0 0 3px ${({ theme }) => theme.color.primary70};
  }
`

const SecondaryStyle = css<{ $isSelected: boolean }>`
  background-color: ${({ theme }) => theme.color.grey07};

  /* double ampersands added to override some old styles
  to be removed later */
  && {
    color: ${({ theme }) => theme.color.bodyText};

    svg {
      fill: ${({ theme }) => theme.color.bodyText};
    }

    ${({ $isSelected }) =>
      $isSelected &&
      css`
        background-color: ${({ theme }) => theme.color.grey10};
      `}

    &:hover {
      text-decoration: none;
      background-color: ${({ theme }) => theme.color.grey10};
    }

    &:active {
      text-decoration: none;
      background-color: ${({ theme }) => theme.color.grey20};
    }

    &.focus-visible {
      border-color: ${({ theme }) => theme.color.grey70};
    }

    &:disabled {
      background-color: ${({ theme }) => theme.color.grey05};
      color: ${({ theme }) => theme.color.grey30};

      > * {
        opacity: 0.3;
      }

      svg {
        fill: ${({ theme }) => theme.color.grey30};
      }
    }
  }
`

export const BaseMenuItem = styled.li`
  list-style: none;
  min-width: 0;
`

export const BaseMenuButtonStyle = css<BaseMenuButtonStyleProps>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${({ theme }) => theme.space.s40};
  box-sizing: border-box;

  white-space: nowrap;
  text-decoration: none;

  padding: ${({ theme }) => theme.space.s8};

  outline: none;
  border: 2px solid transparent;
  border-radius: ${({ theme }) => theme.space.s4};

  cursor: pointer;

  && {
    text-decoration: none;
  }

  ${({ $variant }) => $variant === ButtonVariants.BASIC && BasicStyle};
  ${({ $variant }) =>
    $variant === ButtonVariants.BASIC_SECONDARY && BasicSecondaryStyle};
  ${({ $variant }) =>
    $variant === ButtonVariants.PRIMARY_BASIC && PrimaryBasicStyle};
  ${({ $variant }) => $variant === ButtonVariants.SECONDARY && SecondaryStyle};
  ${({ $variant }) => $variant === ButtonVariants.PRIMARY_GHOST && PrimaryGhostStyle};
`
